import { useTranslation } from 'react-i18next';
import { GripVertical, Pencil, X, Plus } from 'lucide-react';
import { useState } from 'react';
import type { OnboardingData } from './types.js';

interface Props {
  data: OnboardingData;
  onChange: (patch: Partial<OnboardingData>) => void;
  onBack: () => void;
  onNext: () => void;
}

export default function StepStages({ data, onChange, onBack, onNext }: Props) {
  const { t } = useTranslation('onboarding');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [newStageName, setNewStageName] = useState('');

  function updateStage(index: number, name: string) {
    const updated = data.stages.map((s, i) => (i === index ? { ...s, name } : s));
    onChange({ stages: updated });
    setEditingIndex(null);
  }

  function removeStage(index: number) {
    onChange({ stages: data.stages.filter((_, i) => i !== index) });
  }

  function addStage() {
    if (!newStageName.trim()) return;
    onChange({ stages: [...data.stages, { name: newStageName.trim() }] });
    setNewStageName('');
  }

  return (
    <div className="page-enter">
      <h2
        style={{
          fontSize: 'var(--text-2xl)',
          fontWeight: 'var(--weight-light)',
          color: 'var(--color-text)',
          marginBottom: 'var(--space-2)',
          letterSpacing: 'var(--tracking-tight)',
        }}
      >
        {t('stages.title')}
      </h2>
      <p
        style={{
          fontSize: 'var(--text-sm)',
          color: 'var(--color-text-muted)',
          marginBottom: 'var(--space-6)',
        }}
      >
        {t('stages.subtitle')}
      </p>

      <div
        style={{
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
          marginBottom: 'var(--space-4)',
        }}
      >
        {data.stages.map((stage, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              padding: 'var(--space-3) var(--space-4)',
              borderBottom: index < data.stages.length - 1 ? '1px solid var(--color-gray-100)' : 'none',
              background: 'var(--color-bg)',
            }}
          >
            <GripVertical size={16} color="var(--color-gray-300)" style={{ flexShrink: 0, cursor: 'grab' }} />

            {editingIndex === index ? (
              <input
                type="text"
                defaultValue={stage.name}
                autoFocus
                onBlur={(e) => updateStage(index, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') updateStage(index, e.currentTarget.value);
                  if (e.key === 'Escape') setEditingIndex(null);
                }}
                style={{
                  flex: 1,
                  padding: 'var(--space-1) var(--space-2)',
                  border: '1px solid var(--color-brand)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-text)',
                  background: 'var(--color-bg)',
                }}
              />
            ) : (
              <span style={{ flex: 1, fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>
                {stage.name}
              </span>
            )}

            <button
              onClick={() => setEditingIndex(index)}
              aria-label={`Edit stage ${stage.name}`}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-gray-400)', display: 'flex', padding: 4 }}
            >
              <Pencil size={14} />
            </button>
            <button
              onClick={() => removeStage(index)}
              aria-label={`Remove stage ${stage.name}`}
              disabled={data.stages.length <= 1}
              style={{
                background: 'none',
                border: 'none',
                cursor: data.stages.length > 1 ? 'pointer' : 'not-allowed',
                color: data.stages.length > 1 ? 'var(--color-gray-400)' : 'var(--color-gray-200)',
                display: 'flex',
                padding: 4,
              }}
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>

      {/* Add stage */}
      <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-8)' }}>
        <input
          type="text"
          value={newStageName}
          onChange={(e) => setNewStageName(e.target.value)}
          placeholder={t('stages.stage_name_placeholder')}
          onKeyDown={(e) => e.key === 'Enter' && addStage()}
          style={{
            flex: 1,
            padding: 'var(--space-2) var(--space-3)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--text-sm)',
            color: 'var(--color-text)',
            background: 'var(--color-bg)',
          }}
        />
        <button
          onClick={addStage}
          disabled={!newStageName.trim()}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            padding: 'var(--space-2) var(--space-3)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--text-sm)',
            color: newStageName.trim() ? 'var(--color-brand)' : 'var(--color-text-muted)',
            background: 'var(--color-bg)',
            cursor: newStageName.trim() ? 'pointer' : 'not-allowed',
          }}
        >
          <Plus size={14} />
          {t('stages.add_stage')}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
        <button onClick={onBack} style={backBtnStyle}>
          ← {t('actions.back', { ns: 'common' })}
        </button>
        <button onClick={onNext} style={nextBtnStyle}>
          {t('actions.next', { ns: 'common' })} →
        </button>
      </div>
    </div>
  );
}

const backBtnStyle: React.CSSProperties = {
  padding: 'var(--space-3) var(--space-5)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-sm)',
  color: 'var(--color-text-secondary)',
  background: 'var(--color-bg)',
  cursor: 'pointer',
};

const nextBtnStyle: React.CSSProperties = {
  flex: 1,
  padding: 'var(--space-3) var(--space-5)',
  background: 'var(--color-brand)',
  color: 'var(--color-text-inverse)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-sm)',
  fontWeight: 'var(--font-medium)',
  cursor: 'pointer',
};
